import { Dtos, TagId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminEntityTagsQueryKey } from "../entity.keys";

export const useAdminUpdateEntityTag = () => {
	const queryClient = useQueryClient();
	const { patch } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			tagId,
			updatedDisplayName,
		}: {
			tagId: TagId;
			updatedDisplayName: Dtos.Tag.Update.Request;
		}) => {
			return await patch<Dtos.Tag.Update.Response>(`/v2/admin/tags/${tagId}`, updatedDisplayName);
		},
		onMutate: async ({
			tagId,
			updatedDisplayName,
		}: {
			tagId: TagId;
			updatedDisplayName: Dtos.Tag.Update.Request;
		}) => {
			await queryClient.cancelQueries({ queryKey: adminEntityTagsQueryKey() });
			const previousEntityTags = queryClient.getQueryData<Dtos.Tag.GetAll.Response>(
				adminEntityTagsQueryKey(),
			);

			queryClient.setQueryData<Dtos.Tag.GetAll.Response>(adminEntityTagsQueryKey(), (old) => {
				return (
					old
						?.map((tag) => (tag.id === tagId ? { ...tag, ...updatedDisplayName } : tag))
						.sort((a, b) => a.displayName.localeCompare(b.displayName)) ?? []
				);
			});

			return { previousEntityTags };
		},
		onError: (_err, _newEntityTag, context) => {
			queryClient.setQueryData(adminEntityTagsQueryKey(), context?.previousEntityTags);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: adminEntityTagsQueryKey() });
		},
	});

	return {
		updateEntityTag: mutation.mutate,
		updateEntityTagLoading: mutation.isPending,
		updateEntityTagError: mutation.error,
		dismissUpdateEntityTagError: () => {
			mutation.reset();
		},
	};
};
